<!-- eslint-disable max-len -->
<template>
  <div>
    <h2>Payment</h2>
    <template v-if="selectedItems.length > 0">
      <p>
        You are purchasing:
        <ul>
          <li v-for="item in selectedItems" :key="idOf(item && item.gameSet)" class="selected-item">
            {{ displayName(item) }} ({{ formatPrice(item && item.price) }})
          </li>
        </ul>
      </p>
    </template>
    <div v-if="expirationLabel" style="margin-bottom: 15px;">
      <b-alert show variant="info">
        <strong>Content Expiration:</strong> {{ expirationLabel }}
      </b-alert>
    </div>
    <dropin-container
      style="margin-bottom: 10px;"
      :clientToken="clientToken"
      ref="dropinContainer"
      @loaded="onMethodChange"
      @payment-method-changed="onMethodChange"
    />
    <div style="margin-bottom: 10px;">
      <div class="input-group">
        <input
          style="max-width: 200px"
          v-model.trim="couponInput"
          type="text"
          class="form-control"
          placeholder="Coupon Code (optional)"
        >
        <div class="input-group-append">
          <b-button :disabled="processingCoupon || couponInput.length === 0" variant="secondary" @click="applyCoupon">Apply</b-button>
        </div>
      </div>
      <div v-if="couponInput && !coupon" style="font-style: italic; font-size: 14px;">Note that coupon codes are case-sensitive.</div>
      <p v-if="coupon && coupon.coupon" class="coupon-note">Using coupon code {{ coupon.coupon.name }}</p>
      <div v-if="couponError" class="coupon-error">{{ couponError }}</div>
    </div>
    <b-alert v-if="errorMessage" show variant="danger">{{ errorMessage }}</b-alert>
    <div style="text-align: right;">
      <b-button
        style="min-width: 150px;"
        :disabled="price === null || processing || (!paymentMethodReady && price > 0)"
        @click="submitPayment"
      >
        {{ processing ? 'Processing…' : `Pay ${formatPrice(price)}` }}
      </b-button>
    </div>
  </div>
</template>

<script>
import DropinContainer from './DropinContainer';

/** Accepts a populated doc or a raw id and returns the id. */
export function idOf(x) {
  if (!x) return null;
  if (x._id) return x._id;
  if (x.id) return x.id;
  if (typeof x === 'string') return x;
  return x;
}

export default {
  components: {
    DropinContainer
  },
  props: {
    clientToken: { type: String, required: false, default: null },
    price: { type: Number, required: true },
    couponCode: { type: String, required: false, default: '' },
    coupon: { type: Object, required: false, default: null },
    selectedItems: { type: Array, required: true },
    registrationLinkId: { type: String, required: true },
    expirationLabel: { type: String, required: false, default: null },
    errorMessage: { type: String, required: false, default: null }
  },
  data() {
    return {
      couponInput: this.couponCode || '',
      couponError: null,
      processing: false,
      processingCoupon: false,
      methodFlip: false
    };
  },
  computed: {
    paymentMethodReady() {
      // The Braintree drop-in's requestable state is not reactive on its own. `onMethodChange`
      // toggles `methodFlip` on every drop-in event (loaded / payment-method-changed); reading
      // it here registers it as a reactive dependency so this computed re-evaluates and
      // re-reads isPaymentMethodRequestable() whenever the payment method changes.
      // (Same trick as purchase-flow-views/PaymentPage.vue / ClassPurchaseFlow.)
      if (this.methodFlip && !this.methodFlip) { const ex = {}; throw ex; }
      if (this.$refs.dropinContainer && this.$refs.dropinContainer.dropin) {
        return this.$refs.dropinContainer.dropin.isPaymentMethodRequestable();
      }
      return false;
    }
  },
  watch: {
    couponCode() {
      this.couponInput = this.couponCode || '';
      this.couponError = null;
    }
  },
  methods: {
    idOf,
    formatPrice(value) {
      const num = Number(value);
      return isNaN(num) ? '' : `$${Math.max(0, num).toFixed(2)}`;
    },
    displayName(item) {
      const gs = item && item.gameSet;
      if (gs && typeof gs === 'object') {
        const firstGame = (Array.isArray(gs.games) && gs.games.length > 0) ? gs.games[0]._id : null;
        return gs.name || gs.title
          || (firstGame && (firstGame.name || firstGame.title))
          || gs._id || gs.id || '(item)';
      }
      return gs || '(item)';
    },
    onMethodChange() {
      this.methodFlip = !this.methodFlip;
    },
    async applyCoupon() {
      this.processingCoupon = true;
      this.couponError = null;
      this.$emit('coupon-code-change', this.couponInput);
      // The parent re-dispatches getCatalogPrice with the new coupon; nothing
      // else to do here. (Left as a hook for future validation behavior.)
      this.processingCoupon = false;
    },
    async submitPayment() {
      if (!this.$refs.dropinContainer) return;
      const dropin = this.$refs.dropinContainer.dropin;
      if (!dropin) return;

      this.processing = true;
      try {
        const method = await dropin.requestPaymentMethod();
        const resp = await this.$store.dispatch('chargeCatalog', {
          registrationLinkId: this.registrationLinkId,
          itemIds: this.selectedItems.map(item => idOf(item && item.gameSet)),
          couponCode: this.couponCode || undefined,
          paymentNonce: method ? method.nonce : undefined
        });
        this.$emit('charged', {
          success: true,
          transactionId: resp && resp.transactionId,
          expirationDate: resp && resp.expirationDate
        });
      } catch (ex) {
        console.error(ex);
        const data = ex.response && ex.response.data;
        let message = 'Payment failed, please try again.';
        if (typeof data === 'string') {
          message = data;
        } else if (data && typeof data === 'object') {
          message = data.message || data.error || message;
        } else if (ex.message) {
          message = ex.message;
        }
        this.$emit('charged', { success: false, error: message });
      }
      this.processing = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.selected-item
{
  margin-right: 10px;
}

.coupon-note
{
  font-style: italic;
  font-size: 14px;
}

.coupon-error
{
  color: red;
  font-size: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/braintree/CatalogPaymentPage.vue